// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import {
  selectTaxesDisplaySetting,
  selectIsAuthenticatedUser,
} from '@gandi/authentication';
import {
  addProductSuggestionToCart,
  selectCartCurrency,
  useSuggestionTexts,
  type ProductSuggestion,
} from '@gandi/cart';
import { sendCommerce, setProps } from '@gandi/tracking';
import { useTranslator } from '@gandi/translate';

import { useNotifications, useAppSelector } from 'src/hooks';

import { fetchDomainCreateProductsCatalog } from '../../../modules/actions';
import { getDomainContactPayload } from '../modules/actions';

import Suggestion from './Suggestion';

export type Props = {
  suggestion: ProductSuggestion,
  onHide?: (SyntheticEvent<>) => mixed,
};

function useAddSuggestionToCart(suggestion: ProductSuggestion) {
  const translator = useTranslator();
  const dispatch = useDispatch();
  const authenticated = useAppSelector(selectIsAuthenticatedUser);

  return async () => {
    setProps(
      {
        ...suggestion.metadatas,
        product_placement: 'overview',
      },
      {
        events: ['product.add_to_cart'],
      },
    );
    if (suggestion.product_type === 'domain' && authenticated) {
      const payload = await dispatch(
        getDomainContactPayload(translator, suggestion),
      );
      // $FlowFixMe[prop-missing] - ProductPayload + suggestion = ProductSuggestion
      return dispatch(addProductSuggestionToCart({ ...suggestion, payload }));
    }

    return dispatch(addProductSuggestionToCart(suggestion));
  };
}

const DOMAIN_PRODUCTS = ['domains', 'mailboxv2', 'domain_option'];

export default function ProductSuggestionItem({
  suggestion,
  onHide,
}: Props): React.MixedElement {
  const dispatch = useDispatch();
  const translator = useTranslator();
  const { formatPrice } = translator;
  const currency = useAppSelector(selectCartCurrency);
  const taxesIncluded = useAppSelector(selectTaxesDisplaySetting);
  const { title, description, details } = useSuggestionTexts(suggestion);

  const addSuggestionToCart = useAddSuggestionToCart(suggestion);
  const { notifyMaybeApiError } = useNotifications();

  const price = formatPrice(
    taxesIncluded
      ? suggestion.total.after_taxes
      : suggestion.total.before_taxes,
    currency,
  );

  const isProductDomain = DOMAIN_PRODUCTS.includes(suggestion.product_type);

  React.useEffect(() => {
    if (currency && suggestion && price) {
      sendCommerce({
        name: 'product.display',
        data: {
          product_id: suggestion.product_type,
          product: `${suggestion.product_type} - ${suggestion.process}`,
          // $FlowIgnore[incompatible-type] - coerce duration and duration_unit as strings
          product_variant: `${suggestion.payload.duration}${
            suggestion.payload.duration_unit || 'y'
          }`,
          product_brand: isProductDomain
            ? 'Produits registres'
            : 'Produits Gandi',
          product_discount: suggestion.discount_code !== '',
          product_pricetaxincluded: suggestion.total.after_taxes,
          product_pricetaxfree: suggestion.total.before_taxes,
          product_currency: currency,
          product_stock: true,
          product_quantity: suggestion.prices?.length
            ? suggestion.prices[0].quantity
            : 1,
          customObject: {
            ...suggestion.metadatas,
            product_placement: 'overview',
          },
        },
      });
    }
  }, [currency, suggestion, price, isProductDomain]);

  const handleAddToCart = async () => {
    try {
      // $FlowFixMe[incompatible-use] - data is not undefined
      const { data } = await addSuggestionToCart();
      if (suggestion.product_type === 'domain') {
        const reconstructedHackyProduct = {
          ...suggestion,
          ...data,
          fqdn: suggestion.payload.fqdn,
        };

        dispatch(fetchDomainCreateProductsCatalog([reconstructedHackyProduct]));
      }
    } catch (e) {
      notifyMaybeApiError(e);
    }
  };

  return (
    <Suggestion
      title={title}
      description={description}
      price={price}
      onAddToCart={handleAddToCart}
      onHide={onHide}
    >
      {details}
    </Suggestion>
  );
}
